import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logIncident } from "@/lib/incident-log";

export const Route = createFileRoute("/request-access")({
  head: () => ({
    meta: [
      { title: "Request access — VISIKT" },
      {
        name: "description",
        content:
          "VISIKT is invite-only. Request an invitation and we'll email you a sign-up link when your spot is ready.",
      },
      { property: "og:title", content: "Request access — VISIKT" },
      {
        property: "og:description",
        content:
          "Request an invitation to VISIKT, the Norway-first app for subscriptions, salary, and real purchasing power.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: RequestAccessPage,
});

function RequestAccessPage() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [heardAbout, setHeardAbout] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.from("access_requests").insert({
        name: name.trim(),
        email: email.trim(),
        heard_about: heardAbout.trim(),
      });
      if (error) throw error;
      logIncident({
        severity: "info",
        source: "business",
        event_type: "access_request_submitted",
        message: "Access request submitted",
      });
      setDone(true);
    } catch (err) {
      console.error("Access request submit failed:", err);
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(t("requestAccess.errors.submitFailed"));
      logIncident({
        severity: "warn",
        source: "business",
        event_type: "access_request_failed",
        message: msg,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-5 pt-8 pb-12 max-w-md mx-auto w-full flex items-center justify-between gap-3">
        <Link to="/" className="font-display font-extrabold tracking-tighter text-xl">
          VISIKT
        </Link>
        <PublicNav />
      </header>

      <main className="flex-1 px-5 max-w-md mx-auto w-full">
        {done ? (
          <div className="animate-entry space-y-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("requestAccess.eyebrow")}
            </p>
            <h1 className="text-3xl font-display font-bold tracking-tight">
              {t("requestAccess.successHeadline")}
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("requestAccess.successBody")}
            </p>
            <Link
              to="/auth"
              className="inline-block mt-4 text-xs font-mono uppercase tracking-widest text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t("requestAccess.backToSignIn")}
            </Link>
          </div>
        ) : (
          <div className="animate-entry">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              {t("requestAccess.eyebrow")}
            </p>
            <h1 className="text-3xl font-display font-bold tracking-tight mb-2">
              {t("requestAccess.headline")}
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-8">
              {t("requestAccess.body")}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Field label={t("requestAccess.name")}>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("requestAccess.namePlaceholder")}
                  maxLength={100}
                  className="input-base"
                />
              </Field>

              <Field label={t("requestAccess.email")}>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  maxLength={255}
                  className="input-base"
                />
              </Field>

              <Field label={t("requestAccess.heardAbout")}>
                <input
                  type="text"
                  required
                  value={heardAbout}
                  onChange={(e) => setHeardAbout(e.target.value)}
                  placeholder={t("requestAccess.heardAboutPlaceholder")}
                  maxLength={200}
                  className="input-base"
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-md bg-foreground text-background font-medium text-sm disabled:opacity-50 transition-opacity mt-2"
              >
                {loading ? t("requestAccess.submitting") : t("requestAccess.submit")}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/auth"
                className="text-xs font-mono uppercase tracking-widest text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {t("requestAccess.backToSignIn")}
              </Link>
            </div>
          </div>
        )}
      </main>

      <div className="max-w-md mx-auto w-full px-5 pb-8 mt-16">
        <PublicFooter />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
